import { LogOut, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

const ROLE_LABEL = {
  super_admin: "ConfirmTerms Super Admin",
  company_admin: "Vendor/Supplier Administrator",
  customer_admin: "Customer Portal",
};

export const PORTAL_THEMES = {
  navy: {
    band: "bg-[#0d1b3e]",
    kicker: "text-blue-300",
    title: "text-white",
    subtitle: "text-slate-300",
    labelBar: "bg-[#0d1b3e] text-white",
    labelAccent: "text-blue-300",
  },
  grey: {
    band: "bg-[#e6eaf0]",
    kicker: "text-slate-600",
    title: "text-slate-900",
    subtitle: "text-slate-700",
    labelBar: "bg-[#e6eaf0] text-slate-900",
    labelAccent: "text-slate-600",
  },
  teal: {
    band: "bg-[#1f4e5a]",
    kicker: "text-teal-200",
    title: "text-white",
    subtitle: "text-teal-50",
    labelBar: "bg-[#1f4e5a] text-white",
    labelAccent: "text-teal-200",
  },
  light: {
    band: "bg-white",
    kicker: "text-blue-600",
    title: "text-slate-900",
    subtitle: "text-slate-600",
    labelBar: "bg-white text-slate-900",
    labelAccent: "text-blue-600",
  },
};

export const Brand = ({ dark = false }) => (
  <div className="flex items-center gap-2.5">
    <div className="grid h-9 w-9 place-items-center rounded-md bg-blue-600">
      <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.4} />
    </div>
    <div className="leading-none">
      <div
        className={`font-display text-[17px] font-extrabold tracking-tight ${dark ? "text-white" : "text-slate-900"}`}
      >
        Confirm<span className="text-blue-600">Terms</span>
      </div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.22em] text-slate-500">
        Credit Verification
      </div>
    </div>
  </div>
);

export const PortalLabel = ({ label, theme = PORTAL_THEMES.light }) => (
  <div className="flex items-center gap-2.5" data-testid="portal-label">
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-blue-600">
      <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.4} />
    </div>
    <div className="font-display text-[15px] font-extrabold tracking-tight sm:text-[17px]">
      ConfirmTerms <span className={theme.labelAccent}>|</span> {label}
    </div>
  </div>
);

export const Shell = ({
  title,
  subtitle,
  roleLine,
  portalLabel,
  themeName = "light",
  actions,
  children,
}) => {
  const { user, logout, portal, setPortal } = useAuth();
  const theme = PORTAL_THEMES[themeName] || PORTAL_THEMES.light;
  return (
    <div className="min-h-screen bg-slate-50">
      <header className={`sticky top-0 z-30 border-b border-black/10 ${theme.labelBar}`}>
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 lg:px-8">
          <PortalLabel label={portalLabel || ROLE_LABEL[user?.role]} theme={theme} />
          <div className="flex items-center gap-3 sm:gap-5">
            {user?.can_switch_portal && (
              <div
                className="flex rounded-full border border-current/25 p-0.5"
                data-testid="portal-switcher"
              >
                {[
                  ["customer", "My references"],
                  ["company", "My customers"],
                ].map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPortal(key)}
                    data-testid={`switch-to-${key}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors duration-200 ${
                      portal === key ? "bg-blue-600 text-white" : "opacity-70 hover:opacity-100"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold" data-testid="header-user-email">
                {user?.email}
              </div>
              <div className={`text-[11px] uppercase tracking-[0.18em] ${theme.labelAccent}`}>
                {portalLabel || ROLE_LABEL[user?.role]}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={logout}
              data-testid="logout-button"
              className="rounded-full border-current/30 bg-white/10 text-current transition-colors duration-200 hover:bg-white/20"
            >
              <LogOut className="mr-1.5 h-3.5 w-3.5" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className={`border-b border-black/10 ${theme.band}`} data-testid="title-band">
        <div className="mx-auto flex max-w-7xl flex-wrap items-end justify-between gap-5 px-5 py-10 lg:px-8 lg:py-12">
          <div className="ct-rise">
            <div
              className={`text-xs font-semibold uppercase tracking-[0.2em] ${theme.kicker}`}
            >
              {portalLabel || ROLE_LABEL[user?.role]}
            </div>
            <h1
              className={`mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl ${theme.title}`}
            >
              {title}
            </h1>
            {roleLine && (
              <p
                className={`mt-3 max-w-3xl text-base md:text-lg ${theme.subtitle}`}
                data-testid="role-line"
              >
                {roleLine}
              </p>
            )}
            {subtitle && <p className={`mt-2 max-w-3xl text-base ${theme.subtitle}`}>{subtitle}</p>}
          </div>
          {actions}
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8 lg:py-12">{children}</main>
    </div>
  );
};
